import { CreateTeamRequest, CreateTeamResponse } from '@/api';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useCompaniesTranslation, useCompanyPermissions } from '../hooks';
import {
    addOrUpdateAllTeamsCache,
    addOrUpdateCompanyUsersCache
} from '../queries/company-cache';

type UseCreateTeamActionData = {
    /** Whether this viewer can create a team in this company right now. */
    isAllowed: boolean;
    /** Why not, ready to show in a tooltip. Empty while allowed. */
    reason: string;
    createTeam: (data: CreateTeamRequest) => Promise<CreateTeamResponse>;
    loading: boolean;
    error: string;
};

/**
 * Creating a team: whether this viewer may do it, why not when they may not,
 * and the call itself. Kept together so a caller can wire up a button —
 * disabled state, tooltip, spinner and click — from one hook.
 */
export function useCreateTeamAction(): UseCreateTeamActionData {
    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    const { canCreateTeams } = useCompanyPermissions();

    const [isAllowed, reason] = useMemo(() => {
        if (!canCreateTeams) {
            return [false, t('actions.create_team.reason_not_permitted')];
        }

        return [true, ''];
    }, [canCreateTeams, t]);

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error `POST /v3/teams`
    // declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            // Both mean the same to the user: this company is not one they may
            // add a team to.
            if (
                isApiError(error, 'CANNOT_CREATE_TEAM') ||
                isApiError(error, 'COMPANY_NOT_FOUND')
            ) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'USER_NOT_FOUND')) {
                return t('shared.errors.user_not_found');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: (data: CreateTeamRequest) =>
            openApiRef.current.teams
                .createTeam(data)
                .then((response) => response.data),
        onSuccess: (data) => {
            addOrUpdateAllTeamsCache(queryClient, data.data.team);
            addOrUpdateCompanyUsersCache(queryClient, data.data.companyUser);

            UIToast.success(t('actions.create_team.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        isAllowed,
        reason,
        createTeam: (data: CreateTeamRequest): Promise<CreateTeamResponse> =>
            mutation.mutateAsync(data),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
